import '@babel/polyfill';
import ReturnCustomerXsell from "./ReturnCustomerXsell";
import Vuex from 'vuex';
import { shallowMount, createLocalVue } from "@vue/test-utils";
import { getObjProperty } from "../../../../../utilities/mrVueUtils";

const localVue = createLocalVue();
localVue.use(Vuex);

let addToRefillCartMock = jest.fn();
let initRefillCartMock = jest.fn();
let loadRefillCartMock = jest.fn();
let addSubAttachmentMock = jest.fn(() => Promise.resolve());
let getMembershipXsellProductsMock = jest.fn();

function createWrapper(storeObj) {
  let store = new Vuex.Store(storeObj);
  return shallowMount(ReturnCustomerXsell, {
    store,
    localVue,
    provide: {
      getObjProperty
    }
  });
}

function clearMocks() {
  addToRefillCartMock.mockClear();
  initRefillCartMock.mockClear();
  loadRefillCartMock.mockClear();
  addSubAttachmentMock.mockClear();
  getMembershipXsellProductsMock.mockClear();
}

let storeObj = {
  modules: {
    refillCart: {
      namespaced: true,
      state: {
        refillCart: {},
        modifyingRefillProduct: null
      },

      getters: {
        productIdInRefillCart: jest.fn()
      },

      actions: {
        addToRefillCart: addToRefillCartMock,
        initRefillCart: initRefillCartMock,
        loadRefillCart: loadRefillCartMock
      }
    },

    xsell: {
      namespaced: true,
      state: {
        membershipXsellProducts: []
      },

      actions: {
        getMembershipXsellProducts: getMembershipXsellProductsMock
      }
    },

    subscriptions: {
      namespaced: true,
      actions: {
        addSubAttachment: addSubAttachmentMock
      }
    }
  }
};

describe('ReturnCustomerXsell', () => {
  describe('computed', () => {
    describe('items', () => {
      test('show only items that are not in refillCart', () => {
        let localState = {
          membershipXsellProducts: [
            { id: 13 },
            { id: 14 },
            { id: 15 },
            { id: 16 }
          ],

          productIdInRefillCart: jest.fn((id) => [14, 15].includes(id))
        };

        let res = ReturnCustomerXsell.computed.items.call(localState);

        expect(res.length).toBe(2);
        expect(res).toEqual([{ id: 13 }, { id: 16 }]);
      });
    });

    describe('baseSubscription', () => {
      test('return first sub item from subscriptionsInCart', () => {
        let localState = {
          getObjProperty,
          refillCart: {
            subscriptionsInCart: [
              { id: 1234 },
              { id: 2345 },
              { id: 3456 }
            ]
          }
        };

        let res = ReturnCustomerXsell.computed.baseSubscription.call(localState);
        expect(res).toEqual({ id: 1234 });
      });

      test('return empty obj if subscriptionsInCart is an empty Array', () => {
        let localState = {
          getObjProperty,
          refillCart: {
            subscriptionsInCart: []
          }
        };

        let res = ReturnCustomerXsell.computed.baseSubscription.call(localState);
        expect(res).toEqual({});
      });
    });
  });

  describe('methods', () => {
    afterEach(() => {
      clearMocks();
    });

    describe('addProductToRefillCart', () => {
      test('call designated method when attaching to subscription with expected payload', () => {
        let sub_id = 1234;
        let item = { id: 20 };
        let expectedPayload = {
          sub_id,
          product_id: item.id,
          qty: 1,
          ctaSource: 'rcdMembershipXsell'
        };

        //- set sub that will get used in payload
        let storeCopy = Object.assign({}, storeObj);
        storeCopy.modules.refillCart.state.refillCart.subscriptionsInCart = [{ subscription_id: sub_id }];

        let wrapper = createWrapper(storeCopy);

        wrapper.vm.addProductToRefillCart({}, item, true);
        expect(addSubAttachmentMock).toHaveBeenCalledTimes(1);
        expect(addSubAttachmentMock.mock.calls[0][1]).toEqual(expectedPayload);
      });

      test('call designated method when adding to refillCart', () => {
        let item = { id: 30 };
        let expectedPayload = {
          product_id: item.id,
          qty: 1,
          ctaSource: 'rcdMembershipXsell'
        };

        let wrapper = createWrapper(storeObj);
        wrapper.vm.addProductToRefillCart({}, item, false);
        expect(addToRefillCartMock).toHaveBeenCalledTimes(1);
        expect(addToRefillCartMock.mock.calls[0][1]).toEqual(expectedPayload);
      });
    });

    describe('canAttachThisItem', () => {
      test('return false when baseSubscription does NOT exist', () => {
        let wrapper = createWrapper(storeObj);
        let item = { id: 43, product_code: 'gloss' };
        let res = wrapper.vm.canAttachThisItem(item);
        expect(res).toEqual(false);
      });

      test('return false when xsell item product_type is not part of subscription attachments', () => {
        let storeCopy = Object.assign({}, storeObj);
        storeCopy.modules.refillCart.state.refillCart.subscriptionsInCart = [{ subscriptionAttachments: [{ code: 'extra_tube' }] }];
        let wrapper = createWrapper(storeCopy);
        let item = { id: 143, product_code: 'accessory' };
        let res = wrapper.vm.canAttachThisItem(item);
        expect(res).toEqual(false);
      });

      test('return true when xsell item product_type is part of subscription attachments', () => {
        let storeCopy = Object.assign({}, storeObj);
        storeCopy.modules.refillCart.state.refillCart.subscriptionsInCart = [{ subscriptionAttachments: [{ code: 'gloss' }] }];
        let wrapper = createWrapper(storeCopy);
        let item = { id: 143, product_code: 'gloss' };
        let res = wrapper.vm.canAttachThisItem(item);
        expect(res).toEqual(false);
      });
    });

    describe('mounted', () => {
      test('on mounted call these methods', () => {
        createWrapper(storeObj);
        expect(initRefillCartMock).toHaveBeenCalledTimes(1);
        expect(getMembershipXsellProductsMock).toHaveBeenCalledTimes(1);
      });
    });
  });
});